// src/pages/ScoringFundingReadinessDisclaimer.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function ScoringFundingReadinessDisclaimerPage() {
  const disclaimerText = `GWIN SCORING AND FUNDING-READINESS DISCLAIMER
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Purpose of This Disclaimer
Gwin may generate scores, indicators, dashboards, badges, certificates, readiness assessments, progress reports, and recommendations to help users understand and improve their business finance capability.
This Disclaimer explains what Gwin’s scores and funding-readiness indicators mean, what they do not mean, how they should be used, and the limits of relying on them.
This Disclaimer should be read together with Gwin’s:
Terms of Use;
Privacy Policy;
POPIA Consent Notice;
Refund and Cancellation Policy;
any applicable partner, advisor, pilot, or funder referral terms.
By using Gwin, viewing a score, completing an assessment, downloading a report, sharing a certificate, or using any readiness indicator, you agree to this Disclaimer.

2. What Gwin Scores Are
Gwin scores are internal educational, behavioural, progress, and readiness-support indicators.
They may be used to help users understand:
what they have learned;
which financial concepts they understand;
which practical tasks they have completed;
which financial habits they are building;
where they may have readiness gaps;
what actions they may need to take next;
how their business finance capability may be improving over time.
Gwin scores may include, but are not limited to:
Knowledge Score;
Application Score;
Behaviour Score;
Funding-Readiness Score;
Financial Capability Score;
Financial Credibility Score;
progress score;
completion score;
readiness indicator;
dashboard indicator;
badge;
certificate;
unlock level;
recommended next action.

3. What Gwin Scores Are Not
Gwin scores are not:
credit scores;
credit bureau reports;
loan approvals;
investment approvals;
grant approvals;
funding guarantees;
legal opinions;
tax opinions;
accounting opinions;
audited financial statements;
compliance certificates;
tax clearance certificates;
professional qualifications;
accredited qualifications, unless expressly stated in a separate written agreement;
financial advice;
legal advice;
tax advice;
investment advice;
debt counselling;
lending advice;
a guarantee of business success;
a guarantee that a user is financially healthy;
a guarantee that a funder, bank, investor, grant provider, or partner will accept the user.
A high Gwin score does not mean that a user will receive funding.
A low Gwin score does not mean that a user can never receive funding.

4. No Guarantee of Funding
Gwin does not guarantee that any user will receive:
a loan;
credit;
investment;
grant funding;
supplier development funding;
enterprise development support;
sponsorship;
funder approval;
improved credit terms;
reduced interest rates;
financial support from any third party.
Any funding decision remains entirely with the relevant funder, bank, lender, investor, grant provider, sponsor, partner, or financial institution.
A third party may reject a user even if the user:
completes all Gwin lessons;
achieves a high score;
earns a badge;
receives a certificate;
completes a funding-readiness assessment;
downloads a readiness report;
improves their dashboard indicators;
is referred to a funder through Gwin.

5. Gwin Is Not a Credit Provider or Lender
Unless expressly stated otherwise in writing, Gwin is not:
a credit provider;
a bank;
a lender;
a financial institution;
a credit bureau;
a debt counsellor;
a funding decision-maker;
a loan originator;
a guarantor;
an insurer;
an investment adviser;
a tax practitioner;
an accounting firm;
a law firm.
Gwin’s role is to provide educational, behavioural, and readiness-support tools.
If Gwin later introduces funder referrals, funding marketplace features, soft credit checks, document checks, accounting integrations, bank data integrations, or credit-readiness features, those features may be subject to additional terms, consent forms, and regulatory notices.

6. No Financial, Legal, Tax, Accounting, Credit, or Investment Advice
Gwin content, scores, assessments, dashboards, templates, and recommendations are for general education and readiness support only.
They should not be treated as personalised professional advice.
Users should consult qualified professionals before making decisions about:
loans;
credit;
investments;
tax;
accounting;
legal compliance;
debt;
pricing;
employment;
funding applications;
financial statements;
business restructuring;
contracts;
business strategy.
Users remain responsible for their own decisions.

7. How Scores May Be Generated
Gwin may generate scores and indicators using one or more of the following:
lesson completion;
quiz answers;
quiz accuracy;
repeat attempts;
practical assignments;
template completion;
self-reported business information;
business profile information;
behavioural check-ins;
dashboard activity;
cash flow routines;
budgeting routines;
debtor or creditor tracking activity;
compliance-related tasks;
document readiness;
user-submitted files;
programme participation;
partner or advisor inputs, where applicable;
future accounting or bank data integrations, only where consented to and available.
The exact score logic, weighting, rules, calculations, thresholds, data models, and formulas are proprietary to Gwin and may be changed, improved, corrected, or updated from time to time.

8. Scores Depend on Information Provided
Gwin scores may depend partly or fully on information provided by the user.
If a user provides inaccurate, incomplete, outdated, false, misleading, exaggerated, or manipulated information, the resulting score, recommendation, dashboard indicator, certificate, or report may also be inaccurate or misleading.
Users are responsible for ensuring that information submitted to Gwin is truthful, accurate, current, complete, and lawful.
Gwin is not responsible for decisions made by a user or third party based on inaccurate information submitted by the user.

9. Self-Reported Information
Some scores may be based on self-reported information, especially during the MVP, beta, pilot, or early commercial phase.
Self-reported information may not be independently verified by Gwin.
Where scores are based on self-reported information, they should be treated as developmental indicators rather than verified business assessments.
Gwin may later introduce verification features, such as document uploads, advisor review, accounting software integrations, bank data integrations, third-party checks, or funder-specific validation, but these will be subject to additional terms and consent where required.

10. Score Changes and Updates
A user’s score may change over time.
Scores may change because of:
new lessons completed;
improved quiz performance;
additional assignments submitted;
updated business information;
changed scoring logic;
corrected errors;
expired activity;
removal of false or unsupported information;
changes in readiness criteria;
introduction of new features;
partner or programme requirements;
Gwin’s ongoing improvement of the scoring model.
Gwin may update, recalculate, suspend, remove, reset, or invalidate scores where reasonably necessary.

11. Score Manipulation and False Submissions
Users may not manipulate, inflate, falsify, game, or misuse Gwin scores.
Prohibited conduct includes:
submitting false information;
exaggerating business activity;
uploading false documents;
misrepresenting compliance status;
completing activities dishonestly;
asking another person to complete activities on the user’s behalf;
sharing login details;
creating multiple accounts;
using bots or automation;
exploiting bugs;
manipulating templates or dashboards;
misrepresenting a Gwin score to a funder, partner, investor, or third party.
If Gwin suspects score manipulation, we may:
suspend the account;
remove or reset scores;
invalidate certificates;
block funder referrals;
notify relevant partners where lawful;
terminate access;
refuse refunds;
take legal action where appropriate.

12. Score Visibility and Sharing
By default, Gwin scores are intended for the user’s own learning and readiness journey.
Gwin will not share detailed personal scores, readiness indicators, uploaded documents, financial information, bank information, accounting information, or sensitive business information with funders, advisors, partners, or third parties unless:
the user has consented;
sharing is required for a programme and has been clearly disclosed;
the information is aggregated or anonymised;
sharing is required by law;
sharing is necessary to protect Gwin, users, partners, or platform integrity.
Users should not share Gwin scores in a misleading way.
If a user shares a score, certificate, dashboard screenshot, badge, or report with a third party, the user must not present it as a credit score, funding approval, legal certification, tax clearance, audit result, or professional qualification unless expressly authorised by Gwin in writing.

13. Partner, Advisor, and Programme Use
If a user accesses Gwin through an accountant, bookkeeper, business coach, incubator, accelerator, ESD programme, sponsor, employer, funder, or institutional partner, selected progress or completion information may be shared with that partner where disclosed and legally permitted.
Detailed scores, financial information, documents, funding-readiness indicators, bank data, accounting data, or sensitive information will only be shared where lawful and, where required, with the user’s specific consent.
Programme partners may use Gwin data to support:
user progress tracking;
completion monitoring;
cohort reporting;
programme support;
impact reporting;
business development support.
Programme partners may not treat Gwin scores as guaranteed funding approvals or verified credit decisions.

14. Funder Referral Use
If Gwin introduces funder referral or marketplace features, Gwin may help users prepare for, identify, or access potential funding opportunities.
However:
Gwin does not guarantee referral acceptance;
Gwin does not guarantee that a funder will review the user;
Gwin does not guarantee that a funder will approve the user;
Gwin does not guarantee the amount, cost, term, or type of funding;
Gwin does not control third-party funder criteria;
Gwin may receive referral, lead, introduction, or success fees where disclosed and lawful.
Any funder may conduct its own assessment, request additional information, perform credit checks, apply internal rules, and make an independent decision.

15. Soft Credit Checks and Future Verification Features
If Gwin later offers soft credit checks, accounting software integrations, bank data integrations, document verification, tax status checks, or third-party readiness assessments, those features will be governed by additional disclosures and consent forms.
Before using those features, users may be told:
what information will be collected;
which third parties may be involved;
whether the check affects credit records;
how the information will be used;
who may receive the results;
how long information may be stored;
whether refusal affects access to the feature.
Gwin will not conduct credit-related checks or access bank/accounting data without appropriate disclosure and consent where required.

16. Certificates, Badges, and Unlocks
Gwin may issue certificates, badges, levels, XP, unlocks, progress markers, or completion confirmations.
These are intended to recognise platform progress, learning activity, behaviour completion, or readiness milestones.
Unless expressly stated in writing, they are not:
accredited qualifications;
SAQA-recognised qualifications;
SETA certificates;
professional certifications;
compliance confirmations;
funding approvals;
proof of creditworthiness;
proof of financial health.
Users may not alter, forge, sell, or misrepresent Gwin certificates, badges, or reports.

17. Accuracy and Availability of Scores
Gwin aims to provide useful and responsible scoring tools, but we do not guarantee that scores will always be complete, accurate, current, error-free, or suitable for every purpose.
Scores may be affected by:
system errors;
incomplete information;
self-reported data;
user mistakes;
delayed updates;
changed scoring logic;
technical issues;
third-party data errors;
missing integrations;
temporary platform downtime.
Where we identify a material scoring error, we may correct, recalculate, or withdraw the affected score.

18. Limitation of Liability
To the maximum extent allowed by South African law, Gwin is not liable for any loss, damage, claim, rejection, missed opportunity, business decision, financial decision, tax issue, compliance issue, funding outcome, credit outcome, investment outcome, or third-party decision arising from or connected to:
use of a Gwin score;
reliance on a Gwin readiness indicator;
use of a Gwin dashboard;
completion or non-completion of Gwin lessons;
a funder rejecting a user;
a user submitting inaccurate information;
a third party misunderstanding a Gwin score;
changes to scoring logic;
temporary score inaccuracies;
score manipulation by a user;
self-reported information;
platform downtime;
third-party service errors;
user reliance on Gwin as professional advice.
Where liability cannot legally be excluded, Gwin’s liability will be limited as set out in the Terms of Use.

19. User Responsibility
Users remain responsible for:
verifying their own information;
seeking professional advice where necessary;
understanding that Gwin is not a funder;
using scores honestly;
not misleading third parties;
making their own business and financial decisions;
complying with law;
reviewing funder terms independently;
understanding the risks of taking funding or debt.

20. Changes to This Disclaimer
Gwin may update this Disclaimer from time to time.
Updates may be made to reflect:
new scoring features;
new readiness tools;
funder referral features;
regulatory changes;
partner programme requirements;
platform improvements;
risk management needs.
The updated version will apply from the effective date shown at the top.

21. Contact Details
For questions about Gwin scores, readiness indicators, certificates, reports, or this Disclaimer, contact:
Marion Rich Pty Ltd t/a Gwin
Registration number: 022 / 577834 / 07
Support email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za`;

  return (
    <LegalPageLayout
      title="Scoring and Funding-readiness Disclaimer"
      backTo="/contact"
      backLabel="Back"
    >
      <LegalDocumentContent documentText={disclaimerText} />
    </LegalPageLayout>
  );
}